import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { OktaAuth, toRelativeUrl, type OktaAuthOptions } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { getConfig } from "../config";
import {
  AuthReactContext,
  AuthStateFromOkta,
  SANDBOX_AUTH,
} from "./useAuth";
import RequiredAuth from "./RequiredAuth";

export function OktaProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const navigate = useNavigate();
  const config = getConfig();
  const authDisabled = config.authDisabled;

  const oktaAuth = useMemo(() => {
    if (authDisabled) return null;
    const options: OktaAuthOptions = {
      issuer: config.oktaIssuer,
      clientId: config.oktaClientId,
      redirectUri: `${window.location.origin}/login/callback`,
      pkce: true,
    };
    return new OktaAuth(options);
  }, [authDisabled, config.oktaIssuer, config.oktaClientId]);

  const restoreOriginalUri = useMemo(
    () =>
      async (_oktaAuth: OktaAuth, originalUri?: string): Promise<void> => {
        const target = toRelativeUrl(
          originalUri ?? "/",
          window.location.origin,
        );
        navigate(target, { replace: true });
      },
    [navigate],
  );

  if (authDisabled) {
    return (
      <AuthReactContext.Provider value={SANDBOX_AUTH}>
        {children}
      </AuthReactContext.Provider>
    );
  }

  return (
    <Security oktaAuth={oktaAuth!} restoreOriginalUri={restoreOriginalUri}>
      <AuthStateFromOkta>
        <RequiredAuth>{children}</RequiredAuth>
      </AuthStateFromOkta>
    </Security>
  );
}
